import FolderIcon from '@components/Projects/FolderIcon';
import { type FolderSortKey, sortDirection } from '@components/Projects/folderSort';
import Thumbnail from '@components/Projects/Thumbnail';
import { useLocalization } from '@lib/localization';
import {
  Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Typography
} from '@mui/material';
import { type CustomFolderChildRow } from '@components/Projects/folderRows';
import {
  gridSectionsStyles, gridSectionStyles, gridSectionTitleStyles, gridStyles, tableIconStyles, tableNameStyles,
  tableRowStyles, tableSectionRowStyles, tableStyles, tableSubtitleStyles, tableThumbnailStyles, tileBodyStyles,
  tileFolderWidth, tileIconStyles, tileInfoStyles, tileNameStyles, tileSelectedStyles, tileStyles,
  tileThumbnailStyles
} from '@pages/Projects.styles';
import { folderPathOf } from '@store/api/projectEndpoints';

/** A kind of item listed under its own name: the teams' folders, the shared ones, folders, projects. */
export interface FolderSection {
  key: string;
  title: string;
  rows: CustomFolderChildRow[];
}

interface FolderItemsProps {
  /** Only those that hold an item. A listing of one kind of item shows no name over it. */
  sections: FolderSection[];
  /** The item whose menu is open, drawn as selected. */
  activeId?: string;
  /** Search results come from all over the tree, so each one says which folder it is in. */
  showLocation: boolean;
  onOpen: (row: CustomFolderChildRow) => void;
  /** Opens the item's menu, from a right-click, a long press or the keyboard's menu key on it. */
  onMenu: (event: React.MouseEvent<HTMLElement>, row: CustomFolderChildRow) => void;
}

/** A team's own folder and one another team shared carry a sign on their icon, and say which on hover. */
const badgeOf = (row: CustomFolderChildRow, t: (key: string) => string) => {
  if (row.isTeamRoot) return { emblem: 'team' as const, title: `${row.name} · ${t('Folder_Team_Chip')}` };
  if (row.isSharedWithMe) return { emblem: 'shared' as const, title: `${row.name} · ${t('Folder_Shared_Chip')}` };
  return { emblem: undefined, title: row.name };
};

const DateText = ({ value }: { value: string }) => {
  const { currentLanguage } = useLocalization();

  if (!value) return null;

  // The list says how long ago; the exact moment is on hover.
  return <span title={new Date(value).toLocaleString(currentLanguage)}>{new Date(value).toAgoString()}</span>;
};

/** The icon view: each item an icon with its name under it, each section under its own name. */
export const FolderGrid = ({ sections, activeId, showLocation, onOpen, onMenu }: FolderItemsProps) => {
  const { t } = useLocalization();

  return (
    <Box sx={gridSectionsStyles}>
      {sections.map((section) => (
        <Box key={section.key} sx={gridSectionStyles}>
          {sections.length > 1 && <Typography component="h2" sx={gridSectionTitleStyles}>{section.title}</Typography>}
          <Box sx={gridStyles}>
            {section.rows.map((row) => {
              const { emblem, title } = badgeOf(row, t);

              return (
                <Box
                  key={row.id}
                  sx={row.id === activeId ? [tileStyles, tileSelectedStyles] : tileStyles}
                  onContextMenu={(event) => onMenu(event, row)}
                >
                  <Box
                    tabIndex={0}
                    title={title}
                    sx={tileBodyStyles}
                    onClick={() => onOpen(row)}
                    onKeyDown={(event) => { if (event.key === 'Enter') onOpen(row); }}
                  >
                    <Box className="tile-icon" sx={tileIconStyles}>
                      {row.type === 'Folder'
                        ? <FolderIcon size={tileFolderWidth} emblem={emblem} />
                        : <Box sx={tileThumbnailStyles}><Thumbnail projectId={row.id} iconSize={28} /></Box>}
                    </Box>
                    <Typography className="tile-name" sx={tileNameStyles}>{row.name}</Typography>
                    {showLocation && row.type === 'Project' &&
                      <Typography noWrap title={folderPathOf(row.item)} sx={tileInfoStyles}>{folderPathOf(row.item)}</Typography>}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

interface FolderTableProps extends FolderItemsProps {
  /** `null` while the rows are in the order they came in: a search's results, by relevance. */
  sort: FolderSortKey | null;
  onSort: (key: FolderSortKey) => void;
}

/**
 * The list view: one row an item, with its dates and size in columns that sort, and a row with
 * its name over each section. As the list narrows the columns go one by one, the date created
 * first and the date modified last, and then the name carries the date and the size in a line
 * under it (`tableStyles`, by the list's own width). Every row has one cell a column, the section
 * rows too: a column hidden in one row and spanned in another left the table counting it and
 * squeezing the name.
 */
export const FolderTable = ({ sections, activeId, showLocation, sort, onSort, onOpen, onMenu }: FolderTableProps) => {
  const { t, currentLanguage } = useLocalization();

  const sortLabel = (key: FolderSortKey, label: string) => (
    <TableSortLabel
      active={sort === key}
      direction={sortDirection(key)}
      onClick={() => onSort(key)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    // The items scroll under it, so the column names stay in view.
    <Table stickyHeader sx={tableStyles}>
      <TableHead>
        <TableRow>
          <TableCell className="col-name">{sortLabel('name', t('Common_Name'))}</TableCell>
          <TableCell className="col-modified" sx={{ width: 170 }}>{sortLabel('dateModified', t('Common_DateModified'))}</TableCell>
          <TableCell className="col-size" sx={{ width: 120 }}>{sortLabel('size', t('Common_Size'))}</TableCell>
          <TableCell className="col-created" sx={{ width: 170 }}>{sortLabel('dateCreated', t('Common_DateCreated'))}</TableCell>
        </TableRow>
      </TableHead>
      {sections.map((section) => (
        <TableBody key={section.key}>
          {/* A header of its group of rows, as Finder heads its groups. */}
          {sections.length > 1 &&
            <TableRow sx={tableSectionRowStyles}>
              <TableCell component="th" scope="rowgroup" className="col-name">{section.title}</TableCell>
              <TableCell className="col-modified" />
              <TableCell className="col-size" />
              <TableCell className="col-created" />
            </TableRow>}
          {section.rows.map((row) => {
            const { emblem, title } = badgeOf(row, t);
            const sizeText = row.type === 'Folder'
              ? t('Folder_ItemCount', { count: row.itemCount ?? 0 })
              : row.size ? row.size.formatFileSize(currentLanguage) : '—';

            return (
              <TableRow
                key={row.id}
                hover
                selected={row.id === activeId}
                tabIndex={0}
                sx={tableRowStyles}
                onClick={() => onOpen(row)}
                onContextMenu={(event) => onMenu(event, row)}
                onKeyDown={(event) => { if (event.key === 'Enter') onOpen(row); }}
              >
                <TableCell className="col-name">
                  <Stack direction="row" alignItems="center" spacing={1.5} title={title} sx={{ minWidth: 0 }}>
                    {row.type === 'Folder'
                      ? <Box sx={tableIconStyles}><FolderIcon size={28} emblem={emblem} /></Box>
                      : <Box sx={tableThumbnailStyles}><Thumbnail projectId={row.id} iconSize={14} /></Box>}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={tableNameStyles}>{row.name}</Typography>
                      {/* Search results come from all over the tree, so where a project lives is the
                          question being asked; inside a folder its id tells apart two of one name. */}
                      {row.type === 'Project' &&
                        <Typography noWrap className={showLocation ? undefined : 'row-id'} sx={tableSubtitleStyles}>
                          {(showLocation && folderPathOf(row.item)) || row.id}
                        </Typography>}
                      {/* The columns' date and size, for a list too narrow to show them. */}
                      <Typography noWrap className="row-info" sx={tableSubtitleStyles}>
                        {row.dateModified && <><DateText value={row.dateModified} />{' · '}</>}{sizeText}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell className="col-modified"><DateText value={row.dateModified} /></TableCell>
                <TableCell className="col-size">{sizeText}</TableCell>
                <TableCell className="col-created"><DateText value={row.dateCreated} /></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      ))}
    </Table>
  );
};
